import { ErroreApi } from "./client"
import type { StatoPerformance } from "./tipi"

export interface ArtistoInRegia {
  id: string
  name: string
  song: string | null
  /** `null` finché non si è mai esibito in questa serata. */
  stato: StatoPerformance | null
  position: number | null
  performance_id: string | null
}

export interface StatoRegia {
  edizione: { id: string; year: number; name: string; invite_code: string; status: string }
  serate: { id: string; number: number; status: string }[]
  serata: { id: string; number: number; status: string }
  aperta: {
    performance_id: string
    artist_id: string
    name: string
    position: number
    voted: string[]
    pending: string[]
  } | null
  artisti: ArtistoInRegia[]
}

async function regia<T>(percorso: string, codice: string, opzioni: RequestInit = {}): Promise<T> {
  let risposta: Response
  try {
    risposta = await fetch(`/api/admin${percorso}`, {
      ...opzioni,
      headers: { "content-type": "application/json", "X-Admin-Code": codice },
    })
  } catch {
    throw new ErroreApi(0, "Nessuna connessione")
  }
  if (!risposta.ok) {
    const dettaglio = await risposta
      .json()
      .then((c) => (typeof c?.detail === "string" ? c.detail : null))
      .catch(() => null)
    throw new ErroreApi(risposta.status, dettaglio ?? `Errore ${risposta.status}`)
  }
  return risposta.json() as Promise<T>
}

export interface EdizioneInElenco {
  id: string
  year: number
  name: string
  invite_code: string
  status: string
  n_serate: number
  artisti: number
  giurati: number
  serate: { id: string; number: number; status: string }[]
}

export interface GiuratoInGruppo {
  id: string
  user_id: string | null
  nickname: string
  emoji: string
  joined_at: string | null
  last_seen_at: string | null
  excluded: boolean
  /** Solo se il blocco dopo i tentativi sbagliati è ancora in corso. */
  locked_until: string | null
}

export interface Gruppo {
  join_code: string
  giurati: GiuratoInGruppo[]
}

export const apiRegia = {
  /** Da dove parte il pannello: la serata in diretta, o la prima. */
  partenza: (codice: string) => regia<StatoRegia>("/regia", codice),

  stato: (serataId: string, codice: string) =>
    regia<StatoRegia>(`/serate/${serataId}/regia`, codice),

  /** "Adesso canta X": crea l'esibizione se non c'era e chiude la precedente. */
  apri: (serataId: string, artistId: string, codice: string) =>
    regia<{ ok: boolean; position: number }>(`/serate/${serataId}/apri`, codice, {
      method: "POST",
      body: JSON.stringify({ artist_id: artistId }),
    }),

  chiudiVoti: (performanceId: string, codice: string) =>
    regia<{ ok: boolean }>(`/performances/${performanceId}/lock`, codice, { method: "POST" }),

  svela: (performanceId: string, codice: string) =>
    regia<{ ok: boolean }>(`/performances/${performanceId}/reveal`, codice, { method: "POST" }),

  chiudiSerata: (serataId: string, codice: string) =>
    regia<{ ok: boolean }>(`/serate/${serataId}/chiudi`, codice, { method: "POST" }),

  /** "Ho aperto l'artista sbagliato": toglie l'esibizione e i suoi voti. */
  annulla: (performanceId: string, codice: string) =>
    regia<{ ok: boolean; voti_cancellati: number }>(`/performances/${performanceId}`, codice, {
      method: "DELETE",
    }),

  // --- edizioni e artisti ---------------------------------------------------
  // L'app si riusa ogni febbraio, i Big no: la lista si rifà da qui.

  edizioni: (codice: string) =>
    regia<{ edizioni: EdizioneInElenco[] }>("/editions", codice),

  creaEdizione: (
    codice: string,
    dati: { year: number; n_serate: number; name?: string },
  ) =>
    regia<{ id: string; invite_code: string; join_code: string }>("/editions", codice, {
      method: "POST",
      body: JSON.stringify(dati),
    }),

  aggiungiArtisti: (
    edizioneId: string,
    artisti: { name: string; song?: string | null }[],
    codice: string,
  ) =>
    regia<{ inserted: number; saltati: number }>(`/editions/${edizioneId}/artists`, codice, {
      method: "POST",
      body: JSON.stringify(artisti),
    }),

  modificaArtista: (
    artistaId: string,
    dati: { name?: string; song?: string },
    codice: string,
  ) =>
    regia<{ ok: boolean }>(`/artists/${artistaId}`, codice, {
      method: "PATCH",
      body: JSON.stringify(dati),
    }),

  eliminaArtista: (artistaId: string, codice: string) =>
    regia<{ ok: boolean }>(`/artists/${artistaId}`, codice, { method: "DELETE" }),

  /** Chi è nel gruppo dell'edizione, e il link per entrarci. */
  gruppo: (editionId: string, codice: string) =>
    regia<Gruppo>(`/editions/${editionId}/gruppo`, codice),

  rigeneraLink: (editionId: string, codice: string) =>
    regia<{ join_code: string }>(`/editions/${editionId}/link`, codice, { method: "POST" }),

  modificaGiurato: (
    jurorId: string,
    modifica: { nickname?: string; excluded?: boolean },
    codice: string,
  ) =>
    regia<{ ok: boolean }>(`/jurors/${jurorId}`, codice, {
      method: "PATCH",
      body: JSON.stringify(modifica),
    }),

  linkReset: (jurorId: string, codice: string) =>
    regia<{ codice: string; scade: string }>(`/jurors/${jurorId}/reset`, codice, {
      method: "POST",
    }),
}
